/**
 * Coach hours card (DNKADM-0490 / DNKAPI-0708) — the "Coaches can set their own hours"
 * switch on Tenant settings.
 *
 * On, a signed-in coach edits their own weekly hours on My schedule; off, those hours are
 * read-only there and only an owner or admin sets them on the Staff screen. One switch that
 * saves ON ITS OWN when flipped, the same way the Ask Dink card does: `PUT
 * /v2/admin/tenant-settings` carrying only `coachesSetOwnHours` (partial update). Not
 * optimistic — the switch shows the value from the PUT response, so a failed PUT leaves the
 * previous value showing and raises the server's message. It does not refetch the screen
 * (that would remount the booking form and drop unsaved edits).
 *
 * OWNER/ADMIN only: the route is `RequireAdmin`-gated, so a STAFF session never reaches
 * this card, and the server refuses a STAFF PUT with `403` regardless.
 */
import { useState } from 'react';
import { Box, FormControlLabel, Paper, Stack, Switch, Typography } from '@mui/material';
import { Icon } from '@/theme';
import type { ShowToast } from '@/components/toastContext';
import { putTenantSettings } from '@/api/tenantSettings';
import { classifyFormError } from '@/api/screenError';

interface CoachHoursCardProps {
  /** The loaded `coachesSetOwnHours` value. */
  initialEnabled: boolean;
  /** Raise a non-blocking toast. */
  showToast: ShowToast;
}

export const COACH_HOURS_SWITCH_LABEL = 'Coaches can set their own hours';

export function CoachHoursCard({ initialEnabled, showToast }: CoachHoursCardProps) {
  // The SAVED value; only a PUT response moves it.
  const [enabled, setEnabled] = useState(initialEnabled);
  const [saving, setSaving] = useState(false);

  async function handleChange(next: boolean) {
    setSaving(true);
    try {
      const saved = await putTenantSettings({ coachesSetOwnHours: next });
      setEnabled(saved.coachesSetOwnHours);
      showToast(
        saved.coachesSetOwnHours
          ? 'Coaches can now set their own hours'
          : 'Coaches can no longer set their own hours',
        'success',
      );
    } catch (err) {
      const view = classifyFormError(err, 'That value isn’t valid.', {
        toastFallback: 'The settings couldn’t be saved. Please try again.',
        conflictInline: false,
      });
      showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Paper elevation={1} sx={{ overflow: 'hidden' }}>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1.5,
            p: '16px 20px',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            aria-hidden
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: 'background.default',
              color: 'primary.main',
              flexShrink: 0,
            }}
          >
            <Icon name="schedule" sx={{ fontSize: 20 }} />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 500 }}>Coach hours</Typography>
        </Stack>
        <Box sx={{ p: '18px 20px' }}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(event) => void handleChange(event.target.checked)}
                disabled={saving}
              />
            }
            label={COACH_HOURS_SWITCH_LABEL}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            When this is on, coaches set their own weekly hours on My schedule. When it is off, only
            an owner or admin sets them on the Staff screen. Changing hours never moves or cancels a
            booking.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
